import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { getEnvConfig } from "../../../apiConfig/urlConfig/envConfig";
import { DirhamSymbol } from "dirham-symbol";
import {
  gxfeecreateApi,
  gxfeeviewApi,
} from "@/app/apiConfig/services/authAbstractApiLayer";
import { getDocRefId } from "@/app/utils/loginFlow/generateDocRefId";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage";
/* ─────────────────────────── types ─────────────────────────── */

interface FeeFeature {
  feeFeatureId: number;
  feeId: number;
  subFeatureId: number;
  subFeatureName: string;
  featureId: number;
  featureName: string;
  activityId: number;
  activityName: string;
  feeFeatureFrequency: string | null;
  feeAmount: number | null;
  feeCurrencyCode: string | null;
  recordState: "A" | "I";
  checkBox: "Y" | "N";
}

interface FeeData {
  feeId: number;
  feeName: string;
  feeDesc: string;
  feeUsersLimit: number;
  feeAmount: number;
  feeFrequency: string;
  feeCurrencyCode: string;
  feeDenomination: number;
  subscribeFlag: string;
  feeFeature: FeeFeature[];
}

interface FeatureRow {
  label: string;
  essential: boolean;
  plus: boolean;
  advanced: boolean;
}

interface FeatureGroup {
  group: string;
  icon: string;
  rows: FeatureRow[];
}

interface PlanMeta {
  tagline: string;
  popular: boolean;
  colorKey: string;
}

interface SupportCountry {
  code2: any;
  name: string;
  checkBox: "Y" | "N";
}

interface SubscriptionPlanProps {
  formData: any;
  supportCountries: SupportCountry[];
  setsubscription: (value: boolean) => void;
}

/* ─────────────────────────── constants ─────────────────────────── */

const PLAN_IDS = [1, 2, 3];

const PLAN_META: { [key: number]: PlanMeta } = {
  1: {
    tagline: "Perfect for getting started",
    popular: false,
    colorKey: "essential",
  },
  2: {
    tagline: "Most loved by growing teams",
    popular: true,
    colorKey: "plus",
  },
  3: {
    tagline: "For ambitious enterprises",
    popular: false,
    colorKey: "advanced",
  },
};

const TAB_LABEL: { [key: number]: string } = {
  1: "Essential",
  2: "Plus",
  3: "Advanced",
};

const planColors: { [key: string]: string } = {
  essential: "#1A47B8",
  plus: "#D97706",
  advanced: "#15803D",
};

const GROUP_ICONS: { [key: string]: string } = {
  Accounts: "🏦",
  Cards: "💳",
  Payments: "💸",
  Collections: "📥",
  "Service Request": "🛠️",
  "ERP Systems": "🔗",
  POS: "🛒",
  Loyalty: "🎁",
  "HR Management": "👥",
};

const VISIBLE_FEATURE_COUNT = 4;

/* ─────────────────────────── helpers ─────────────────────────── */

function buildFeatureGroups(allPlansData: {
  [key: number]: FeeData;
}): FeatureGroup[] {
  const groupMap: {
    [key: string]: { icon: string; rows: { [key: string]: FeatureRow } };
  } = {};

  const planKeyMap: { [key: number]: string } = {
    1: "essential",
    2: "plus",
    3: "advanced",
  };

  PLAN_IDS?.forEach((planId) => {
    const data = allPlansData[planId];
    if (!data) return;

    const planKey = planKeyMap[planId];

    data?.feeFeature?.forEach((f) => {
      const groupName = f.featureName;

      if (!groupMap[groupName]) {
        groupMap[groupName] = {
          icon: GROUP_ICONS[groupName] || "⚙️",
          rows: {},
        };
      }

      const rowKey = f.subFeatureName;

      if (!groupMap[groupName].rows[rowKey]) {
        groupMap[groupName].rows[rowKey] = {
          label: f.subFeatureName,
          essential: false,
          plus: false,
          advanced: false,
        };
      }

      if (f.checkBox === "Y") {
        const row = groupMap[groupName].rows[rowKey];
        if (planKey === "essential") row.essential = true;
        else if (planKey === "plus") row.plus = true;
        else if (planKey === "advanced") row.advanced = true;
      }
    });
  });

  return Object.entries(groupMap)?.map(([group, val]) => ({
    group,
    icon: val.icon,
    rows: Object.values(val.rows),
  }));
}

/* ─────────────────────────── sub-components ─────────────────────────── */

const CheckIcon = ({ colorKey }: { colorKey: string }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle
      cx="8"
      cy="8"
      r="8"
      fill={planColors[colorKey]}
      fillOpacity="0.12"
    />
    <path
      d="M4.5 8L7 10.5L11.5 6"
      stroke={planColors[colorKey]}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashIcon = () => <span className="gx-compare-dash">—</span>;

const PlanCardSkeleton = () => (
  <div className="gx-plan-card gx-plan-card-skeleton">
    {[20, 14, 36, 28, 14, 14, 14, 14].map((h, i) => (
      <div
        key={i}
        className="gx-skeleton-block"
        style={{ height: h, marginBottom: 8 }}
      />
    ))}
  </div>
);

/* ─────────────────────────── main component ─────────────────────────── */

const SubscriptionPlan = ({
  formData,
  supportCountries,
  setsubscription,
}: SubscriptionPlanProps) => {
  const navigate = useNavigate();

  const [allPlansData, setAllPlansData] = useState<{
    [key: number]: FeeData;
  }>({});
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const [expandedPlans, setExpandedPlans] = useState<{
    [key: number]: boolean;
  }>({});

  const userIdIDP = sessionStorage.getItem("userIdIDP") || "";
  const { routingTypeCode } = getEnvConfig();

  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleTabClick = (id: number) => {
    setSelectedTab((prev) => (prev === id ? null : id));
  };

  const toggleExpandPlan = (id: number) => {
    setExpandedPlans((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const visiblePlanIds =
    selectedTab === null
      ? PLAN_IDS
      : PLAN_IDS.filter((id) => id === selectedTab);

  const handleChoosePlan = async (feeId: number) => {
    try {
      const supportedCountries = supportCountries
        .map((country) => country?.code2)
        .join(",");

      const date = new Date(formData.tradeLicenseExpiry);

      const payload = {
        groupCompName: formData.companyName,
        emailId: formData.email,
        mobileCode: formData.mobileDialCode,
        mobileNo: formData.mobile,
        addressLine1: formData.address,
        addressLine2: "",
        addressLine3: "",
        addressLine4: "",
        contactName: formData.contactName,
        companyLandline: formData.landline,
        companyPoBox: formData.poBox,
        countryCode2: formData?.country ?? "",
        tradeLicenseExpiryDate: new Date(formData.tradeLicenseExpiry)
          .toISOString()
          .replace("Z", "+00:00"),
        tradeLicenseNumber: formData?.tradeLicenseNumber,
        supportedCountries,
        feeId,
        routingTypeCode: routingTypeCode ?? "CB",
        crtdBy: userIdIDP ?? null,
        docRefId: getDocRefId(),
        type: "FO",
        groupCompType: "",
        groupCompFavourite: "",
      };

      const res = await gxfeecreateApi(payload);

      if (res.status === 1) {
        setAlert({
          type: "success",
          message: "Plan selected successfully.",
        });
        navigate("/onboarding/verificationProgress");
      }
    } catch (err: any) {
      setAlert({
        type: "error",
        message: err?.response?.data?.errorMessage || "Something went wrong.",
      });
    }
  };

  const fetchAllPlans = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        PLAN_IDS.map((id) => gxfeeviewApi({ feeId: id })),
      );

      const mapped: { [key: number]: FeeData } = {};
      results.forEach((res, idx) => {
        const data: FeeData = res?.data ?? res;
        if (data && Array.isArray(data.feeFeature)) {
          mapped[PLAN_IDS[idx]] = data;
        }
      });

      setAllPlansData(mapped);
    } catch (err) {
      console.error("Error fetching subscription plans:", err);
      setAllPlansData({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPlans();
  }, []);

  const isDataReady = !loading && Object.keys(allPlansData).length === 3;

  const featureGroups: FeatureGroup[] = isDataReady
    ? buildFeatureGroups(allPlansData)
    : [];

  const handleBack = () => {
    setsubscription(false);
  };

  return (
    <div className="gx-smeonboard-card">
      <div className="gx-title-bank">
        <button
          type="button"
          onClick={handleBack}
          className="gx-back-button"
          aria-label="Go back"
        >
          <img src={`${ICON_BASE_PATH}backicon.svg`} alt="backicon" />
        </button>
        <h2 className="gx-h2-l">
          Select{" "}
          <span className="gx-mpin-heading-bold gx-h2-b">
            Subscription Plan
          </span>
        </h2>
      </div>

      <div className="gx-smeonboard-overflow">
        <div className="gx-subscription-hero">
          <h3 className="gx-subscription-hero-heading">
            Banking Built For Your Business
          </h3>
          <p className="gx-subscription-hero-subtitle">
            Start free for 30 days. No commitments, no hidden fees.
            <br />
            Just powerful financial tools from day one.
          </p>
          <div className="gx-subscription-badges">
            {[
              { icon: "🔥", text: "30 days free trial" },
              { icon: "💳", text: "No credit card required" },
              { icon: "🚫", text: "Cancel anytime" },
            ].map((b) => (
              <span key={b.text} className="gx-subscription-badge-item">
                <span>{b.icon}</span> {b.text}
              </span>
            ))}
          </div>
        </div>

        <div className="gx-subscription-tabs">
          {PLAN_IDS?.map((id) => (
            <button
              key={id}
              type="button"
              disabled
              className={
                selectedTab === id || selectedTab === null
                  ? `gx-subscription-tab-btn gx-tab-active-${PLAN_META[id].colorKey}`
                  : "gx-subscription-tab-btn"
              }
              onClick={() => handleTabClick(id)}
            >
              {allPlansData[id]?.feeName ?? TAB_LABEL[id]}
            </button>
          ))}
        </div>

        <div
          className="gx-subscription-plans-grid"
          style={{ alignItems: "stretch" }}
        >
          {loading
            ? PLAN_IDS.map((id) => <PlanCardSkeleton key={id} />)
            : PLAN_IDS.filter((id) => visiblePlanIds.includes(id)).map((id) => {
                const featureData = allPlansData[id];
                const meta = PLAN_META[id];
                if (!featureData) return null;

                const activeFeatures =
                  featureData?.feeFeature?.filter((f) => f.checkBox === "Y") ??
                  [];

                const isExpanded = !!expandedPlans[id];
                const hiddenCount = Math.max(
                  activeFeatures.length - VISIBLE_FEATURE_COUNT,
                  0,
                );
                const featuresToShow = isExpanded
                  ? activeFeatures
                  : activeFeatures.slice(0, VISIBLE_FEATURE_COUNT);
                const displayPrice = featureData?.feeAmount;
                const usersLabel =
                  featureData.feeUsersLimit === 0
                    ? "Unlimited Users"
                    : `Upto ${featureData.feeUsersLimit} Users`;

                return (
                  <div
                    key={id}
                    className={`gx-plan-card gx-plan-card-${id}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {meta.popular && (
                      <div className="gx-plan-popular-badge">MOST POPULAR</div>
                    )}

                    <div className={`gx-plan-label gx-plan-label-${id}`}>
                      {featureData.feeName.toUpperCase()}
                    </div>

                    <div className="gx-plan-tagline">{featureData.feeDesc}</div>

                    <div className="gx-plan-price-row">
                      <span className="gx-plan-price-amount">
                        <DirhamSymbol />
                        {displayPrice}
                      </span>
                      <span className="gx-plan-price-unit">
                        {featureData.feeCurrencyCode} /
                        {featureData.feeFrequency}
                      </span>
                    </div>

                    <div
                      className={`gx-plan-user-badge gx-plan-user-badge-${id}`}
                    >
                      {usersLabel}
                    </div>

                    <ul className="gx-plan-features-list">
                      {featuresToShow.map((f) => (
                        <li
                          key={`${id}-${f.subFeatureId}`}
                          className="gx-plan-feature-item"
                        >
                          <CheckIcon colorKey={meta.colorKey} />
                          {f.subFeatureName}
                        </li>
                      ))}
                      {hiddenCount > 0 && (
                        <li
                          className="gx-plan-feature-item gx-plan-feature-more"
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleExpandPlan(id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleExpandPlan(id);
                            }
                          }}
                          aria-expanded={isExpanded}
                          style={{ cursor: "pointer" }}
                        >
                          {isExpanded
                            ? "Show less"
                            : `+${hiddenCount} more features`}
                        </li>
                      )}
                    </ul>

                    <div style={{ marginTop: "auto" }}>
                      <button
                        type="button"
                        className="gx-plan-cta-btn"
                        onClick={() => handleChoosePlan(id)}
                      >
                        CHOOSE {featureData.feeName.toUpperCase()} PLAN
                      </button>

                      <div className="gx-plan-cta-note">
                        Free for 30 days — then {displayPrice}
                        {featureData.feeCurrencyCode}/mo
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>

        {alert && (
          <div className="col-span-12">
            <RequiredMessage message={alert.message} />
          </div>
        )}

        {isDataReady && (
          <div className="gx-subscription-compare">
            <div className="gx-compare-heading-wrap">
              <h3 className="gx-compare-heading">
                Compare <strong>All Features</strong>
              </h3>
              <p className="gx-compare-subtext">
                Everything included across all plans
              </p>
            </div>

            <table className="gx-compare-table">
              <thead>
                <tr>
                  <th className="gx-compare-th-label">Features</th>
                  <th className="gx-compare-th-essential">
                    {allPlansData[1]?.feeName ?? "Essential"}
                  </th>
                  <th className="gx-compare-th-plus">
                    {allPlansData[2]?.feeName ?? "Plus"}
                  </th>
                  <th className="gx-compare-th-advanced">
                    {allPlansData[3]?.feeName ?? "Advanced"}
                  </th>
                </tr>

                <tr>
                  <td className="gx-compare-meta-label">Monthly Price</td>
                  {PLAN_IDS.map((id) => {
                    const d = allPlansData[id];
                    const price = d?.feeAmount;

                    return (
                      <td key={id} className="gx-compare-meta-value">
                        <DirhamSymbol /> {price}
                        <span className="gx-compare-meta-unit">/mo</span>
                      </td>
                    );
                  })}
                </tr>

                <tr>
                  <td className="gx-compare-meta-label">Included Users</td>
                  {PLAN_IDS.map((id) => {
                    const d = allPlansData[id];
                    const users = d
                      ? d.feeUsersLimit === 0
                        ? "Unlimited"
                        : `Upto ${d.feeUsersLimit}`
                      : "—";

                    return (
                      <td key={id} className="gx-compare-meta-value">
                        {users}
                      </td>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {featureGroups.map((group) => (
                  <React.Fragment key={group.group}>
                    <tr className="gx-compare-group-row">
                      <td colSpan={4}>
                        {group.icon} {group.group}
                      </td>
                    </tr>

                    {group.rows.map((row) => (
                      <tr
                        key={`${group.group}-${row.label}`}
                        className="gx-compare-row"
                      >
                        <td className="gx-compare-row-label">{row.label}</td>

                        <td className="gx-compare-row-cell">
                          {row.essential ? (
                            <CheckIcon colorKey="essential" />
                          ) : (
                            <DashIcon />
                          )}
                        </td>

                        <td className="gx-compare-row-cell">
                          {row.plus ? (
                            <CheckIcon colorKey="plus" />
                          ) : (
                            <DashIcon />
                          )}
                        </td>

                        <td className="gx-compare-row-cell">
                          {row.advanced ? (
                            <CheckIcon colorKey="advanced" />
                          ) : (
                            <DashIcon />
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPlan;
